// Portfolio adapters (FRONTEND_RULES §2, §7) — the single camelCase ↔ snake_case
// boundary for the Portfolio screen. Mirrors dashboard.adapters.js:
//   • mapAPIToUI       — maps the static snake_case MOCK to the UI shape (mock mode
//                        + the live baseline). Components never see a snake_case key.
//   • mapUIToAPI       — write direction stub (kept symmetric for future mutations).
//   • mapPortfolioResponse — the LIVE boundary: maps the real backend /portfolio
//                        contract (already camelCase, but structured differently)
//                        to the UI model. In live mode NO value comes from mock:
//                        everything is read or derived from real data. Some values
//                        live on sibling endpoints for the same account, so the
//                        slice also passes /dashboard (return tiles + YTD-growth)
//                        and /manage-account (election change date) as `extras`.
//                        The genuinely-unmodeled editorial fields (weighted-expense
//                        industry/savings, equity vol/beta/yield) stay null → the UI
//                        renders "—" (see BACKEND TODO). The vs-market note is the
//                        exception: its number is real (YTD gap) and the sentence is
//                        composed on the frontend (vsMarketNote).
//
// Why the live mapper exists: the backend splits `funds` (master: nav, expense,
// returns) from `holdings` (positions: balance, shares, %, election) — the UI uses
// one merged list — and ships performanceSeries / moneySources / fundDetails /
// classSparklines where the UI wants compare / sources / fundDetail / classSpark.
import { formatDate, evenTickIndices } from '../../../utils/format';
import { mapFundDetails, mapFundDocuments } from '../../../utils/fundDetail';
import { wholePercents } from '../../../utils/holdings';
// Pending balances live in one place now (FM-2116): both heroes read the same model,
// so the normalization, the status vocabulary and buildBalance are shared rather than
// duplicated per module.
import {
  round2, buildBalance, normalizePendingItems, pendingFromActivity, pendingFromRebalance,
} from '../../../utils/pending';

const round1 = (n) => Math.round(n * 10) / 10;

// ────────── Holdings, loans and allocation drift (FM-2110) — shared ──────────
// Ported from the Participant Redesign standalone: `plan-holdings.js` (holdings
// structure) and `plan-alloc.js` (allocation disambiguation). Keeping the design's
// own model — rather than an equivalent of our own — is what lets the page match
// it exactly, so the vocabulary below is deliberately theirs.
//
// A plan's holdings are not always a flat list. Some hold a MODEL — a managed
// blend the advisor rebalances — which takes one line at the top level and owns
// its underlying funds; others hold 12+ individual funds, a single investment, or
// nothing but models. All of it comes through one uniform item:
//
//   { type: 'fund' | 'model', key, name, ticker, assetClass, balance,
//     balancePct, electionPct, weightInModel, color, children: [ same shape ] }
//
// Top level = models + standalone funds (that is what the allocation viz shows).
// Underlying funds appear only when a model is expanded. `holdingLeaves` flattens
// to the real funds, which is what the asset-class / source / performance surfaces
// consume.
//
// LOANS: an item may carry type 'loan'. A loan is never a holding — it is filtered
// out of every list, chart, total and export.

/** `type` value that marks a participant loan. */
export const LOAN_FUND_TYPE = 'loan';

/**
 * Points of |drift| at which a holding is flagged as off its election.
 * Design: plan-alloc.js DRIFT_THRESHOLD.
 */
export const DRIFT_THRESHOLD_PCT = 5;

/**
 * A participant loan is a receivable, not an investment: it carries a balance but
 * no allocation, no return and no election. It is dropped at this boundary instead
 * of being guarded against in every component. Reads the wire (`fund_type`), the UI
 * (`fundType`) and the design's (`type`) spellings so it works either side of a
 * mapper.
 */
export const isLoanFund = (f) =>
  String(f?.type ?? f?.fundType ?? f?.fund_type ?? '').toLowerCase() === LOAN_FUND_TYPE;

/** Investable positions only — loans removed. */
export const investableFunds = (funds = []) => funds.filter((f) => !isLoanFund(f));

// ── The two percentages, kept apart (design: plan-alloc.js) ──
// Every option has two and they are NOT the same:
//   balancePct  — share of the CURRENT balance sitting in that option
//   electionPct — share of FUTURE contributions directed to it
// A bare "Alloc %" reads as either, so both are labelled everywhere and By Fund
// shows the two side by side.
const num = (v) => (typeof v === 'number' && isFinite(v) ? v : 0);

export const balancePct = (f) => num(f && (f.balancePct != null ? f.balancePct : f.pct));
export const electionPct = (f) => num(f && (f.electionPct != null ? f.electionPct : f.election));

/**
 * Whether this plan takes participant-directed future contributions. Where it
 * doesn't — a cash-balance plan's guaranteed benefit, an inactive plan with no
 * payroll feed — the election side reads "n/a", never 0%, which would wrongly
 * imply an election of zero was made.
 */
export const electionsApply = (plan) => {
  if (typeof plan?.acceptsElections === 'boolean') return plan.acceptsElections;
  // No permission set on record ⇒ assume elections apply. Defaulting the other way
  // would tell a self-directed participant their plan takes no elections, which is
  // both wrong and unrecoverable from the UI (design: plan-alloc.js electionsApply).
  if (!plan?.actions) return true;
  return plan.actions.canChangeElections === true;
};

/** "0.05%" / "99.95%" / "30%": two decimals, as Classic's Source View prints them (FM-3085). */
export const pctText = (v) => `${Number(num(v).toFixed(2))}%`;

/** `pctText`, except a held sliver the view rounded to 0.00 reads "<0.01%", never "0%" (FM-3085). */
export const sourcePctText = (pct, balance) => (balance > 0 && num(pct) < 0.005 ? '<0.01%' : pctText(pct));

/**
 * An allocation share as the screen prints it (FM-3056/FM-3084): the whole-number `displayPct`
 * the adapter computed so the lines add to 100%; a held fund that rounds to 0 reads "<1%".
 */
export const shareText = (f) => {
  if (f?.displayPct == null) return pctText(balancePct(f));
  return f.displayPct === 0 && f.balance > 0 ? '<1%' : `${f.displayPct}%`;
};

/**
 * Splits `total` across `weights` in whole cents that add back to `total` exactly
 * (largest remainder), so a breakdown's lines always sum to the figure above them (FM-3064).
 */
export const allocateCents = (total, weights = []) => {
  const cents = Math.round(Math.abs(num(total)) * 100);
  const ws = weights.map((w) => Math.max(0, num(w)));
  const sum = ws.reduce((s, w) => s + w, 0);
  if (!sum) return ws.map(() => 0);
  const raw = ws.map((w) => (w / sum) * cents);
  const out = raw.map(Math.floor);
  let left = cents - out.reduce((s, c) => s + c, 0);
  const order = raw.map((r, i) => [r - out[i], i]).sort((a, b) => b[0] - a[0]);
  for (let k = 0; left > 0; k += 1, left -= 1) out[order[k % order.length][1]] += 1;
  const sign = num(total) < 0 ? -1 : 1;
  return out.map((c) => (sign * c) / 100);
};

/** Display string for the current-balance share. Always a number. */
export const balanceText = (f) => shareText(f);

/** Display string for the future-contribution election, or "n/a". */
export const electionText = (f, plan) =>
  electionsApply(plan) ? `${electionPct(f)}%` : 'n/a';

/** Column totals for the table footer. */
export const allocTotals = (items = [], plan) => ({
  // The sum of the shares the rows print — 100% for the whole account (FM-3056).
  balanceText: `${Math.round(items.reduce((s, f) => s + (f.displayPct ?? balancePct(f)), 0))}%`,
  electionText: electionsApply(plan)
    ? `${Math.round(items.reduce((s, f) => s + electionPct(f), 0))}%`
    : 'n/a',
});

// ── Drift ──
// Elections say where NEW money goes; the balance drifts away from them as markets
// move. Drift = current share of balance − future election. Positive means you hold
// more of it than you elected.
//
// An option only HAS drift if an election exists for it. A plan that doesn't take
// participant elections has none at all, so its whole column reads "no drift on
// record" (—) rather than 0, which would claim every holding is exactly on target.
export const hasElection = (f, plan) => {
  if (!electionsApply(plan)) return false;
  if (!f) return false;
  return f.electionPct != null || f.election != null;
};

export const driftPts = (f, plan) => {
  if (!hasElection(f, plan)) return null;
  return Math.round((balancePct(f) - electionPct(f)) * 10) / 10;
};

export const isDrifted = (f, plan) => {
  const d = driftPts(f, plan);
  return d !== null && Math.abs(d) >= DRIFT_THRESHOLD_PCT;
};

/** "+6" / "−5" / "0" / "—". Points, so no % suffix (design: plan-alloc.js). */
export const driftText = (f, plan) => {
  const d = driftPts(f, plan);
  if (d === null) return '—';
  if (d === 0) return '0';
  return (d > 0 ? '+' : '−') + Math.abs(d);
};

/**
 * "8 funds" / "1 fund" / "2 managed models" / "3 holdings · 1 managed model".
 * Design: plan-holdings.js countLabel. Counts TOP-LEVEL items, so a model counts
 * once however many funds it holds.
 */
export const countLabel = (items = [], t) => {
  const n = items.length;
  const models = items.filter((i) => i.type === 'model').length;
  if (!models) return t(n === 1 ? 'portfolio.byFund.fundCountOne' : 'portfolio.byFund.fundCountMany', { count: n });
  if (models === n) {
    return t(models === 1 ? 'portfolio.byFund.modelCountOne' : 'portfolio.byFund.modelCountMany', { count: models });
  }
  return t(models === 1 ? 'portfolio.byFund.mixedCountOne' : 'portfolio.byFund.mixedCountMany', {
    count: n,
    models,
  });
};

/** Flatten to the funds actually held — a model contributes its children. */
export const holdingLeaves = (items = []) =>
  items.flatMap((it) => (it.type === 'model' && it.children?.length ? it.children : [it]));

/**
 * Build the holdings list — top-level items sorted by balance, descending.
 * A model's balance and balancePct are the sum of the members actually held, never
 * a figure the payload merely declared; when the two disagree the claim is kept on
 * `declaredBalance` with `reconciled: true` so the mismatch stays visible to tests
 * and support instead of silently overriding real positions.
 *
 * Members carry `weightInModel` — their share OF THE MODEL, which is what the
 * expanded rows show — and `isModelChild`, which suppresses their drift.
 * A model with no held members is not a holding and is dropped; a fund pointing at
 * a model the payload never declared stays top-level rather than disappearing.
 *
 * @param {Array} funds UI-shaped funds (loans are dropped here)
 * @param {Array} models UI-shaped models ({ modelId, name, balance, desc })
 */
export const buildHoldingsTree = (funds = [], models = []) => {
  const held = investableFunds(funds);
  const membersByModel = new Map();
  held.forEach((f) => {
    if (!f.modelId) return;
    if (!membersByModel.has(f.modelId)) membersByModel.set(f.modelId, []);
    membersByModel.get(f.modelId).push(f);
  });

  const items = [];
  const claimed = new Set();

  (models || []).forEach((m) => {
    const members = membersByModel.get(m.modelId);
    if (!members || !members.length) return; // declared but nothing held
    members.forEach((f) => claimed.add(f));
    const balance = round2(members.reduce((s, f) => s + (f.balance || 0), 0));
    const declared = m.balance == null ? null : round2(m.balance);
    const pct = round1(members.reduce((s, f) => s + (f.pct || 0), 0));
    // The model's own return per horizon: balance-weighted over the members that
    // report one (FM-3056 — the row printed a dash while every member had a figure).
    const weightedReturn = (key) => {
      const reporting = members.filter((f) => f[key] != null && f.balance);
      const weight = reporting.reduce((s, f) => s + f.balance, 0);
      return weight ? round2(reporting.reduce((s, f) => s + f[key] * f.balance, 0) / weight) : null;
    };
    const returns = Object.fromEntries(
      ['mtd', 'oneMo', 'threeMo', 'ytd', 'oneYr', 'threeYr', 'fiveYr', 'tenYr'].map((k) => [k, weightedReturn(k)]),
    );
    const weights = wholePercents(members.map((f) => f.balance));
    items.push({
      ...returns,
      type: 'model',
      key: m.modelId,
      modelId: m.modelId,
      ticker: m.ticker ?? null,
      name: m.name,
      // A model's own line reads "Managed model" in the asset-class column — it
      // spans classes, so no single one of them describes it.
      assetClass: m.assetClass ?? 'Managed model',
      desc: m.desc ?? null,
      color: m.color ?? members[0]?.color ?? null,
      balance,
      declaredBalance: declared,
      reconciled: declared != null && declared !== balance,
      pct,
      balancePct: pct,
      // Contributions are directed AT the model, so its election is its own share
      // unless the payload states otherwise — that keeps the footer's election
      // total at 100% and makes a model that holds what it was elected read 0 drift.
      electionPct: m.election ?? pct,
      lineup: m.lineup || [],
      ytd: m.ror ?? returns.ytd,
      expense: null,
      children: members.map((f, i) => ({
        ...f,
        key: f.ticker,
        type: 'fund',
        isModelChild: true,
        balancePct: f.pct,
        // A fund inside a model isn't elected individually — the model is. Default
        // to its own share so it reads as on-target rather than wildly adrift.
        electionPct: f.election ?? f.pct,
        // Exact share for chart geometry; `weightLabel` is the whole-number share the
        // labels print, adding to 100 across the model (FM-3056).
        weightInModel: balance ? round1(((f.balance || 0) / balance) * 100) : 0,
        weightLabel: weights[i] === 0 && f.balance > 0 ? '<1%' : `${weights[i]}%`,
      })),
    });
  });

  held.forEach((f) => {
    if (claimed.has(f)) return;
    items.push({
      ...f,
      type: 'fund',
      key: f.ticker,
      balancePct: f.pct,
      electionPct: f.election,
      weightInModel: null,
      children: [],
    });
  });

  // What the screen prints (FM-3056/FM-3084): whole-number shares from the balances that
  // add to exactly 100, a model's members splitting the model's own share the same way.
  const shown = wholePercents(items.map((it) => it.balance));
  items.forEach((it, i) => {
    it.displayPct = shown[i];
    if (it.type !== 'model') return;
    const split = wholePercents(it.children.map((c) => c.balance), shown[i]);
    it.children.forEach((c, j) => { c.displayPct = split[j]; });
  });

  return items.sort((a, b) => (b.balance || 0) - (a.balance || 0));
};

// ───────────────────────── Mock (snake_case) → UI ─────────────────────────
const mapReturn = (r) => ({ pct: r.pct, amount: r.amount });

const mapFund = (f) => ({
  ticker: f.ticker,
  name: f.name,
  assetClass: f.asset_class,
  assetClassKey: f.asset_class_key,
  family: f.family,
  // 'loan' ⇒ a receivable, dropped from holdings by investableFunds (FM-2110).
  fundType: f.fund_type ?? null,
  // The managed model this position belongs to; null ⇒ a standalone holding.
  modelId: f.model_id ?? null,
  balance: f.balance,
  pct: f.pct,
  election: f.election,
  shares: f.shares,
  nav: f.nav,
  ytd: f.ytd,
  // Multi-period returns (Performance tab); absent on lighter plans → undefined.
  mtd: f.mtd,
  oneMo: f.one_mo,
  threeMo: f.three_mo,
  oneYr: f.one_yr,
  threeYr: f.three_yr,
  fiveYr: f.five_yr,
  tenYr: f.ten_yr,
  expense: f.expense,
  color: f.color,
});

// Asset-class buckets (By Asset Class tab); `tickers` reference the funds list.
const mapAssetClasses = (rows) =>
  rows &&
  rows.map((c) => ({
    key: c.key,
    label: c.label,
    desc: c.desc,
    pct: c.pct,
    balance: c.balance,
    tickers: c.tickers,
  }));

/**
 * Dollar value of the vested share, for the hero's vested chip (which states the
 * amount as well as the percentage, matching the Dashboard hero).
 *
 * Money sources carry their OWN vesting schedule — employee money is always 100%,
 * the employer match may be partway up a graded schedule — so summing them is the
 * accurate figure and is preferred wherever sources exist. Plans that ship only a
 * plan-level `vestedPct` (the lighter mock plans, and any live account whose
 * money-source list is empty) fall back to applying that percentage to the
 * balance. Neither available ⇒ null, and the chip states the percentage alone.
 * @param {Array} sources mapped money sources ({ total, vested })
 * @param {number|null} balance plan balance
 * @param {number|null} vestedPct plan-level vested percentage
 */
/**
 * The mock's balance model, on the same FM-2116 footing as the live one: the mock's
 * `summary.balance` is the headline, so `settled` is it minus the pending net. A mock
 * that instead added pending ON TOP would show a plan's balance as larger in mock mode
 * than in live mode, for the same plan and the same pending row.
 * @param {object} api one plan object from MOCK_API.plans
 */
const mockBalance = (api) => {
  const items = api.pending_items != null
    ? normalizePendingItems(api.pending_items)
    : pendingFromActivity(api.activity);
  const net = round2(items.reduce((sum, i) => sum + (i.amount || 0), 0));
  const headline = api.summary.balance;
  return buildBalance(
    headline == null ? null : round2(headline - net),
    api.summary.as_of ?? null,
    items,
  );
};

const vestedBalanceFrom = (sources, balance, vestedPct) => {
  const rows = (sources || []).filter((s) => s && s.total != null && s.vested != null);
  if (rows.length) return round2(rows.reduce((sum, s) => sum + s.total * (s.vested / 100), 0));
  if (balance == null || vestedPct == null) return null;
  return round2(balance * (vestedPct / 100));
};

// Money sources (By Source tab); `share` proportionally splits each fund balance.
const mapSources = (rows) =>
  rows &&
  rows.map((s) => ({
    key: s.key,
    name: s.name,
    color: s.color,
    total: s.total,
    contrib: s.contrib,
    ytd: s.ytd,
    growth: s.growth,
    vested: s.vested,
    // Plan's vesting schedule in words ("3-year cliff · vests Mar 2027"), when the
    // payload carries one — the By-Source row appends it to the vesting pill.
    vestingNote: s.vesting_note ?? null,
    desc: s.desc,
    share: s.share,
  }));

/**
 * Which documents a fund publishes IN THIS PLAN, as the ids the Fund Detail overlay
 * renders tabs for, in its display order. Two wire shapes are accepted, because the
 * two payloads disagree: the live `/portfolio` fundDetails ship one OBJECT per
 * document (`{ type: 'factSheet' | 'prospectus' | 'summaryProspectus', label, url }`),
 * while the fixture payloads ship the ids directly.
 *
 * `summaryProspectus` is dropped on purpose — it duplicates the full prospectus, the
 * same call FundDetailModal documents. A fund naming nothing recognisable returns
 * undefined rather than an empty list, which the overlay reads as "assume both": an
 * empty array would render a fund with no documents at all.
 */
export { mapFundDocuments };

// Fund fact-sheet detail (Fund Detail overlay), keyed by ticker. The compare
// period keys (1Yr/3Yr/5Yr/10Yr) and mix/holdings are passed through as-is.
const mapFundDetail = (byTicker) =>
  byTicker &&
  Object.fromEntries(
    Object.entries(byTicker).map(([ticker, d]) => [
      ticker,
      {
        benchmark: d.benchmark,
        category: d.category,
        objective: d.objective,
        assets: d.assets,
        inception: d.inception,
        turnover: d.turnover,
        costPer1000: d.cost_per_1000,
        fee12b1: d.fee_12b1,
        salesCharge: d.sales_charge,
        mix: d.mix,
        compare: d.compare,
        holdings: d.holdings,
        // Which documents this fund publishes IN THIS PLAN ('fact' | 'pros'), which
        // decides the overlay's tab strip. Absent ⇒ the overlay assumes both.
        documents: mapFundDocuments(d.documents),
      },
    ]),
  );

// compare/compare_dates keys (1M…5Y) + you/spx/labels are already camelCase-safe;
// map shallowly to keep the snake↔camel boundary explicit.
//
// AXIS LABELS. LineChart spreads `labels` evenly over the points — tick j sits on
// point round((n-1)·j/(L-1)). Hand-authored labels only line up when (n-1) divides
// evenly by (L-1), and even then only if the wording happens to match the point
// underneath: Acme's YTD series put "Feb" on Jan 29 and "Apr" on Mar 19, and the
// five-point plans put "14d ago" on the 7-day point. Since every range ships a
// per-point date, the labels are derived from those dates at the same indices the
// chart will tick on — so a tick always names the point it stands on. Ranges with
// no dates keep their authored labels.
const axisLabels = (points, dates) => {
  if (!Array.isArray(dates) || dates.length !== points) return null;
  return evenTickIndices(points, 5).map((i) => dates[i]);
};

const mapCompare = (c, datesByRange) =>
  c &&
  Object.fromEntries(
    Object.entries(c).map(([range, s]) => [
      range,
      {
        you: s.you,
        spx: s.spx,
        labels: axisLabels((s.you || []).length, datesByRange?.[range]) || s.labels,
      },
    ]),
  );

const mapActivity = (rows) =>
  (rows || []).map((a) => ({
    id: a.id,
    type: a.type,
    ticker: a.ticker,
    desc: a.desc,
    date: a.date,
    amount: a.amount,
    signed: a.signed,
    // Carry settlement status so RecentActivity can flag not-yet-settled rows the
    // same way the hero does (FM-2098) — otherwise a pending row reads as posted.
    status: a.status ?? null,
  }));

const mapElections = (e) => e && { changedDate: e.changed_date, allocatedPct: e.allocated_pct };

// ───────────────── Benchmark inputs (FM-2101) — the backend seam ─────────────────
// What portfolio.benchmark needs beyond the fields already on the UI model:
//   applicable       — the backend's applicability flag (false for cash-balance
//                      plans, and for model-driven accounts — the latter superseded
//                      by FM-2101, which gives models their own benchmark).
//   planType         — plan type when a payload names it; live payloads rely on the
//                      flag above instead.
//   employmentStatus — 'terminated' ⇒ past-performance framing, no forward compare.
//   models           — managed models the participant holds.
//   composition / compositionByRange / seriesByRange
//                    — the FINISHED blend. The backend doesn't ship it yet, so these
//                      stay null and the selector computes the blend. When the
//                      backend adds its benchmark object, map it onto these three
//                      keys and the selector uses it as-is — no UI change needed.
const emptyBenchmarkMeta = () => ({
  applicable: null,
  planType: null,
  employmentStatus: null,
  models: [],
  composition: null,
  compositionByRange: null,
  seriesByRange: null,
});

const mapModel = (m) => ({
  modelId: m.modelId ?? m.model_id,
  name: m.name,
  ticker: m.ticker ?? null,
  pct: m.pct,
  balance: m.balance,
  // Editorial fields the holdings list renders on a model row: the "Managed model"
  // class label, the note shown under an expanded model, its own reported return,
  // and its swatch color.
  assetClass: m.asset_class ?? m.assetClass ?? null,
  desc: m.desc ?? null,
  ror: m.ror ?? null,
  color: m.color ?? null,
  funds: m.funds || [],
  // The MODEL-level direction (absent = merely held) and the model's published
  // composition, so a directed-but-unfunded model can still say what it invests in.
  election: m.election ?? null,
  lineup: m.lineup || [],
});

// Participant action permissions (design: plan-actions.js). Every action a
// participant can request is gated by a plan flag; a plan that doesn't allow one
// simply doesn't render it — no disabled buttons, no dead ends. `canChangeElections`
// is also what decides whether elections (and therefore drift) exist at all.
const ACTION_FLAGS = [
  'canChangeRate',
  'canAutoIncrease',
  'canChangeElections',
  'canRollover',
  'canDistribute',
  'canRebalance',
  'canTransfer',
  'canRequestLoan',
  'canViewStatements',
  'canManageBeneficiaries',
];

// What a plan is assumed to allow when NOTHING is on record. The backend doesn't
// ship permissions yet, and defaulting every flag to false would hide the Manage
// link, the Future-contributions card and the whole drift column on every live
// account — a participant would have no way to tell a real restriction from a
// missing field. The design does the same thing: plan-actions.js `flagsFor` falls
// back to a standard self-directed plan for any id it doesn't know.
// An EXPLICIT `{}` from the backend still means "nothing granted".
const DEFAULT_ACTIONS = {
  canChangeRate: true,
  canAutoIncrease: true,
  canChangeElections: true,
  canRebalance: true,
  canTransfer: true,
  canViewStatements: true,
  canManageBeneficiaries: true,
};

const mapActions = (a) => {
  const src = a == null ? DEFAULT_ACTIONS : a;
  const out = {};
  ACTION_FLAGS.forEach((f) => {
    out[f] = src[f] === true;
  });
  out.recommended = src.recommended ?? null;
  return out;
};

const mapHoldings = (h) =>
  h && {
    ytdGrowth: {
      total: h.ytd_growth.total,
      breakdown: h.ytd_growth.breakdown.map((b) => ({
        key: b.key,
        label: b.label,
        amount: b.amount,
      })),
    },
    vsMarket: {
      youPct: h.vs_market.you_pct,
      youBarPct: h.vs_market.you_bar_pct,
      spxPct: h.vs_market.spx_pct,
      spxBarPct: h.vs_market.spx_bar_pct,
      note: h.vs_market.note,
    },
    weightedExpense: {
      pct: h.weighted_expense.pct,
      industryPct: h.weighted_expense.industry_pct,
      savingsYr: h.weighted_expense.savings_yr,
    },
    equityExposure: {
      equityPct: h.equity_exposure.equity_pct,
      bondPct: h.equity_exposure.bond_pct,
      vol: h.equity_exposure.vol,
      beta: h.equity_exposure.beta,
      yield: h.equity_exposure.yield,
    },
  };

/**
 * Map the static snake_case mock plan to the camelCase UI model. Used in mock mode
 * and as the live baseline.
 * @param {object} api one plan object from MOCK_API.plans
 */
export const mapAPIToUI = (api) => {
  // Loans are split off here so nothing downstream can render one; `funds` is the
  // investable list every widget reads, `holdingsTree` its model-aware grouping.
  const allFunds = (api.funds || []).map(mapFund);
  const funds = investableFunds(allFunds);
  const models = (api.models || []).map(mapModel);
  const sources = mapSources(api.sources);

  return {
    planId: api.plan_id,
    planName: api.plan_name,
    summary: {
      balance: api.summary.balance,
      fundCount: api.summary.fund_count,
      vestedPct: api.summary.vested_pct,
      vestedBalance: vestedBalanceFrom(sources, api.summary.balance, api.summary.vested_pct),
      returns: {
        today: mapReturn(api.summary.returns.today),
        mtd: mapReturn(api.summary.returns.mtd),
        ytd: mapReturn(api.summary.returns.ytd),
        oneYr: mapReturn(api.summary.returns.one_yr),
      },
    },
    // Hero balance model (FM-2098). The mock has no as-of field — the page falls
    // back to yesterday() — and pending items come from its activity rows.
    // FM-2116 semantics, same as the live path below: `summary.balance` is the
    // headline the participant is shown, and it ALREADY INCLUDES in-flight money, so
    // `settled` is carved out beneath it. Applying this to the mock too is what keeps
    // mock and live stating the same figure for the same plan.
    balance: mockBalance(api),
    funds,
    // Excluded positions, kept off `funds` on purpose. Exposed so a future "Loans"
    // surface has a source without reopening the holdings list to them.
    loanFunds: allFunds.filter(isLoanFund),
    models,
    holdingsTree: buildHoldingsTree(funds, models),
    holdings: mapHoldings(api.holdings),
    compare: mapCompare(api.compare, api.compare_dates),
    compareDates: api.compare_dates,
    activity: mapActivity(api.activity),
    elections: mapElections(api.elections),
    // ── Gating the page reads (FM-2110) ──
    // planActive false ⇒ an ended plan: read-only holdings.
    planStatus: api.status ?? 'active',
    planActive: (api.status ?? 'active') !== 'inactive',
    // Per-plan participant permissions. Every action the page offers is gated on
    // one of these; `canChangeElections` also decides whether elections — and so
    // the drift column and the Future-contributions card — exist at all.
    actions: mapActions(api.actions),
    // Phase-3 holdings tabs + fund detail (absent on lighter plans → undefined).
    assetClasses: mapAssetClasses(api.asset_classes),
    sources,
    fundDetail: mapFundDetail(api.fund_detail),
    classSpark: api.class_spark,
    // Benchmark inputs (FM-2101) — the mock mirrors the live contract.
    benchmarkMeta: {
      ...emptyBenchmarkMeta(),
      applicable: api.benchmark?.applicable ?? null,
      planType: api.benchmark?.plan_type ?? null,
      employmentStatus: api.benchmark?.employment_status ?? null,
      models: (api.benchmark?.models || []).map(mapModel),
    },
  };
};

// Inverse mapper — stub for future writes (kept symmetric so write paths have a
// home when the backend arrives).
export const mapUIToAPI = (ui) => ({
  plan_id: ui.planId,
  plan_name: ui.planName,
  summary: ui.summary && {
    balance: ui.summary.balance,
    fund_count: ui.summary.fundCount,
    vested_pct: ui.summary.vestedPct,
  },
});

// ───────────────────────── Live backend → UI ─────────────────────────
// The /portfolio contract is already camelCase but structured differently from
// the UI model. These maps translate the backend's enum/semantic values into the
// keys the widgets expect.

// Backend transaction category → RecentActivity's TYPES icon key.
const ACTIVITY_TYPE = {
  contrib: 'contribution',
  rebal: 'rebalance',
  div: 'dividend',
  dist: 'dividend',
  transfer: 'transfer',
  benef: 'transfer',
};

// Money-source key → brand color (when the backend omits displayColor). Mirrors
// the mock's monochromatic ramp; covers cash-balance keys (paycredit/interest) too.
const SOURCE_COLOR = {
  pretax: '#0A1A3A',
  match: '#0077CC',
  roth: '#8EC0EA',
  paycredit: '#0A1A3A',
  interest: '#0077CC',
};

// Asset-class key → fixed-income flag / equity weight, for deriving the equity vs
// bond split (the only equity-exposure field the backend can support). Mixed
// classes (target-date glide path, balanced) get an approximate equity weight.
const FIXED_INCOME = new Set(['bond', 'tips', 'cash']);
const EQUITY_WEIGHT = { targetDate: 0.9, balanced: 0.6 };

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

// Balance-weighted vested % across money sources (real → replaces mock vestedPct).
const deriveVested = (sources = []) => {
  const total = sources.reduce((s, m) => s + (m.total || 0), 0);
  if (!total) return null;
  return Math.round(sources.reduce((s, m) => s + (m.total || 0) * (m.vested || 0), 0) / total);
};

// "% allocated" = sum of the per-fund contribution elections (real → was mock).
const deriveAllocated = (positions = []) =>
  positions.length
    ? Math.round(positions.reduce((s, h) => s + (h.currentElectionPct || 0), 0))
    : null;

// vs-market sentence — composed on the frontend from the real YTD figures (the
// backend ships no editorial note). Only the gap is data; the phrasing is ours.
const vsMarketNote = (you, spx) => {
  const diff = round2(you - spx);
  const pts = Math.abs(diff).toFixed(1);
  if (diff >= 0.05) return `Outpacing the S&P 500 by ${pts} points this year.`;
  if (diff <= -0.05) return `Trailing the S&P 500 by ${pts} points this year.`;
  return 'Tracking the S&P 500 closely this year.';
};

// vs-market: your vs benchmark YTD return = last point of the YTD performance
// series; bar widths normalize to the benchmark. Returns null when the series is
// absent (performanceSeries is optional on the contract).
const deriveVsMarket = (series = []) => {
  const ytd = series.find((p) => p.period === 'YTD');
  const you = ytd?.youReturns?.[ytd.youReturns.length - 1];
  const spx = ytd?.benchmarkReturns?.[ytd.benchmarkReturns.length - 1];
  if (you == null || spx == null) return null;
  return {
    youPct: you,
    spxPct: spx,
    youBarPct: spx ? clamp(Math.round((you / spx) * 100), 0, 100) : 0,
    spxBarPct: 100,
    note: vsMarketNote(you, spx), // composed on the frontend from the real YTD gap
  };
};

// Holdings-viz color ramp (the design's monochromatic blue, darkest → lightest).
// The backend's per-fund displayColor encodes a stable fund identity, NOT per-plan
// allocation — so the holdings bar/treemap is colored here by balance rank instead
// (matches the design/mock: largest holding = darkest). displayColor is not used.
const FALLBACK_COLORS = [
  '#0A1A3A', '#10295E', '#1B4283', '#0077CC', '#3593D6', '#5BA8E0', '#7BB9E5', '#8EC0EA',
];

// Linear-interpolate between two #rrggbb hexes (t∈[0,1]).
const lerpHex = (a, b, t) => {
  const ch = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const [ar, ag, ab] = ch(a);
  const [br, bg, bb] = ch(b);
  const mix = (x, y) =>
    Math.round(x + (y - x) * t)
      .toString(16)
      .padStart(2, '0');
  return `#${mix(ar, br)}${mix(ag, bg)}${mix(ab, bb)}`;
};

// Sample the ramp continuously at fraction t∈[0,1] (piecewise-linear through the
// stops). N=8 lands exactly on the 8 stops; larger lineups get distinct in-between
// shades instead of snapping several bars to the same stop.
const rampColor = (t) => {
  const L = FALLBACK_COLORS.length;
  if (t <= 0) return FALLBACK_COLORS[0];
  if (t >= 1) return FALLBACK_COLORS[L - 1];
  const pos = t * (L - 1);
  const i = Math.floor(pos);
  return lerpHex(FALLBACK_COLORS[i], FALLBACK_COLORS[i + 1], pos - i);
};

// Color each fund by its balance rank along the ramp (top holding = darkest,
// smallest = lightest). Continuous, so the dark→light range holds and neighbours
// stay distinguishable at any fund count. Mutates the UI funds in place.
const assignRampByBalance = (fundsUI) => {
  const n = fundsUI.length;
  [...fundsUI]
    .sort((a, b) => (b.balance || 0) - (a.balance || 0))
    .forEach((f, rank) => {
      f.color = rampColor(n <= 1 ? 0 : rank / (n - 1));
    });
};

// A complete, mock-free UI scaffold. mapPortfolioResponse fills it from real data;
// any section the backend omits stays at these empty/null defaults (the components
// render "—" / empty states), so no mock value leaks into live mode.
const emptyPortfolioUI = () => ({
  planId: null,
  planName: null,
  asOf: null,
  summary: {
    balance: null,
    fundCount: 0,
    vestedPct: null,
    vestedBalance: null,
    returns: {
      today: { pct: null, amount: null },
      mtd: { pct: null, amount: null },
      ytd: { pct: null, amount: null },
      oneYr: { pct: null, amount: null },
    },
  },
  balance: { total: null, settled: null, pending: 0, asOf: null, pendingItems: [] },
  funds: [],
  loanFunds: [],
  models: [],
  holdingsTree: [],
  planStatus: 'active',
  planActive: true,
  actions: mapActions(null),
  holdings: {
    ytdGrowth: { total: null, breakdown: [] },
    vsMarket: { youPct: null, spxPct: null, youBarPct: 0, spxBarPct: 0, note: null },
    weightedExpense: { pct: null, industryPct: null, savingsYr: null },
    equityExposure: { equityPct: null, bondPct: null, vol: null, beta: null, yield: null },
  },
  compare: {},
  compareDates: {},
  activity: [],
  elections: { changedDate: null, allocatedPct: null },
  assetClasses: [],
  sources: [],
  fundDetail: {},
  classSpark: {},
  benchmarkMeta: emptyBenchmarkMeta(),
});

/**
 * Map the live backend payloads onto the UI model, built from scratch (mock-free):
 * `api` (/portfolio) is the core; `dashboard` supplies the summary return tiles,
 * YTD-growth and plan name; `manage` supplies the election change date. Any field
 * no endpoint carries stays null/empty (renders "—"). Returns an empty scaffold
 * when there's no payload.
 * @param {object|null} api raw /portfolio response
 * @param {{dashboard?: object|null, manage?: object|null}} extras enrichment payloads
 */
export const mapPortfolioResponse = (api, { dashboard, manage } = {}) => {
  const ui = emptyPortfolioUI();
  if (!api) return ui;

  const {
    account = {},
    funds = [],
    holdings = [],
    assetClasses,
    moneySources,
    fundDetails,
    performanceSeries,
    classSparklines,
    activity,
  } = api;

  // ── funds: join the master fund (nav/expense/returns) with its position
  //    (balance/shares/%/election), keyed by ticker ──
  const positionByTicker = new Map(holdings.map((h) => [h.ticker, h]));
  if (funds.length) {
    const joined = funds.map((f) => {
      const h = positionByTicker.get(f.ticker) || {};
      return {
        ticker: f.ticker,
        name: f.name,
        assetClass: f.assetClassLabel,
        assetClassKey: f.assetClassKey,
        family: f.family,
        // Loan / model membership can ride on either the master fund or the
        // position, so read both (FM-2110).
        fundType: f.fundType ?? h.fundType ?? null,
        modelId: h.modelId ?? f.modelId ?? null,
        balance: h.balance ?? 0,
        pct: h.pctOfPortfolio ?? 0,
        election: h.currentElectionPct ?? 0,
        shares: h.shares ?? 0,
        nav: f.nav,
        ytd: f.ytd,
        mtd: f.mtd,
        oneMo: f.oneMo,
        threeMo: f.threeMo,
        oneYr: f.oneYr,
        threeYr: f.threeYr,
        fiveYr: f.fiveYr,
        tenYr: f.tenYr,
        perfAsOf: f.perfAsOf ?? null,
        expense: f.expenseRatio,
        color: null, // assigned by balance rank below (magnitude ramp, not displayColor)
      };
    });
    // Loans never join the holdings list, and they're removed BEFORE the color ramp
    // and the weighted-expense math below so a receivable can't skew either.
    ui.loanFunds = joined.filter(isLoanFund);
    ui.funds = investableFunds(joined);
    assignRampByBalance(ui.funds);
  }

  // Managed models → the model-aware holdings tree (same builder as mock mode).
  ui.models = (api.models || []).map(mapModel);
  ui.holdingsTree = buildHoldingsTree(ui.funds, ui.models);
  // The same printed share wherever a fund is listed flat (By Asset Class, tooltips).
  const shownPct = new Map(holdingLeaves(ui.holdingsTree).map((f) => [f.ticker, f.displayPct]));
  ui.funds = ui.funds.map((f) => (shownPct.has(f.ticker) ? { ...f, displayPct: shownPct.get(f.ticker) } : f));

  // ── Gating (FM-2110). Participant permissions aren't on the contract yet, so
  //    they read through opportunistically and fall back to a standard
  //    self-directed plan (see DEFAULT_ACTIONS) — otherwise every live account
  //    would lose its Manage link, its Future-contributions card and its drift
  //    column to a field the backend simply hasn't shipped. An ended employment
  //    relationship is the one signal the backend does send, and it stands in for
  //    an inactive plan. ──
  ui.actions = mapActions(api.actions ?? dashboard?.account?.actions);
  ui.planStatus =
    api.planStatus ??
    (String(dashboard?.account?.employmentStatus || '').toLowerCase() === 'terminated'
      ? 'inactive'
      : 'active');
  ui.planActive = ui.planStatus !== 'inactive';

  // ── summary: real balance + fund count + derived vested %. The four return
  //    tiles are seeded null here and filled from /dashboard in the cross-endpoint
  //    block below (/portfolio carries no portfolio-level returns). ──
  ui.summary = {
    ...ui.summary,
    balance: account.totalBalance ?? ui.summary.balance,
    // Investable positions only — a participant loan is not a fund you hold.
    fundCount: ui.funds.length || ui.summary.fundCount,
    vestedPct: deriveVested(moneySources),
    // Real money sources carry per-source vesting, so this sums them rather than
    // applying one blended percentage to the whole balance.
    vestedBalance: vestedBalanceFrom(moneySources, account.totalBalance ?? null, deriveVested(moneySources)),
    returns: {
      today: { pct: null, amount: null },
      mtd: { pct: null, amount: null },
      ytd: { pct: null, amount: null },
      oneYr: { pct: null, amount: null },
    },
  };

  // As-of date is real (account.asOfDate); the page falls back to yesterday().
  ui.asOf = account.asOfDate || null;

  // ── hero balance model (FM-2098). Pending money comes from not-yet-settled
  //    activity rows; an in-flight rebalance comes from /manage-account (net-zero,
  //    so it contributes a processing note rather than a dollar amount). ──
  {
    const items = api.pendingItems != null
      ? normalizePendingItems(api.pendingItems)
      : [...pendingFromActivity(activity), ...pendingFromRebalance(manage?.rebalanceState)];
    const net = round2(items.reduce((sum, i) => sum + (i.amount || 0), 0));
    ui.balance = buildBalance(
      account.totalBalance == null ? null : round2(account.totalBalance - net),
      ui.asOf,
      items,
    );
  }

  // ── asset classes ──
  if (Array.isArray(assetClasses) && assetClasses.length) {
    // A class prints the sum of its funds' printed shares, so its card adds up and a fund
    // reads the same in every tab; a class with no fund rows rounds on its own (FM-3056).
    const classPct = wholePercents(assetClasses.map((c) => c.balance));
    ui.assetClasses = assetClasses.map((c, i) => {
      const members = (c.tickers || []).filter((tk) => shownPct.get(tk) != null);
      return {
        key: c.key,
        label: c.label,
        desc: c.description,
        pct: c.pctOfPortfolio,
        displayPct: members.length ? members.reduce((s, tk) => s + shownPct.get(tk), 0) : classPct[i],
        balance: c.balance,
        tickers: c.tickers,
      };
    });
  }

  // ── holdings overview cards. Headlines are derived from real data; the small
  //    footer metrics + the YTD-growth and vs-market values aren't in the contract
  //    and are blanked rather than mocked (BACKEND TODO). ──
  ui.holdings = { ...ui.holdings };

  // Weighted expense: headline = balance-weighted expense ratio (real). The
  // industry benchmark + annual savings are editorial → blank.
  const totalBal = ui.funds.reduce((s, f) => s + (f.balance || 0), 0);
  const weightedPct =
    totalBal > 0
      ? round2(ui.funds.reduce((s, f) => s + (f.balance || 0) * (f.expense || 0), 0) / totalBal)
      : null;
  ui.holdings.weightedExpense = { pct: weightedPct, industryPct: null, savingsYr: null };

  // Equity exposure: equity/bond split derived from asset classes (real). The
  // vol/beta/yield risk stats aren't in the contract → blank.
  let equityPct = null;
  let bondPct = null;
  if (Array.isArray(ui.assetClasses) && ui.assetClasses.length) {
    equityPct = Math.round(
      ui.assetClasses.reduce((s, c) => {
        const w = FIXED_INCOME.has(c.key) ? 0 : EQUITY_WEIGHT[c.key] ?? 1;
        return s + (c.pct || 0) * w;
      }, 0),
    );
    bondPct = 100 - equityPct;
  }
  ui.holdings.equityExposure = { equityPct, bondPct, vol: null, beta: null, yield: null };

  // When the backend serves the real objects, prefer them over the derived values
  // above (per-field merge so a partial object / missing key degrades to "—", not a
  // crash). No-op while the backend omits them.
  if (api.weightedExpense) {
    ui.holdings.weightedExpense = {
      pct: api.weightedExpense.pct ?? ui.holdings.weightedExpense.pct,
      industryPct: api.weightedExpense.industryPct ?? null,
      savingsYr: api.weightedExpense.savingsYr ?? null,
    };
  }
  if (api.equityExposure) {
    ui.holdings.equityExposure = {
      equityPct: api.equityExposure.equityPct ?? ui.holdings.equityExposure.equityPct,
      bondPct: api.equityExposure.bondPct ?? ui.holdings.equityExposure.bondPct,
      vol: api.equityExposure.vol ?? null,
      beta: api.equityExposure.beta ?? null,
      yield: api.equityExposure.yield ?? null,
    };
  }

  // YTD growth — seeded blank; filled from /dashboard.balanceGrowthBreakdown in the
  // cross-endpoint block below when available.
  ui.holdings.ytdGrowth = { total: null, breakdown: [] };

  // vs-market %s + bars derived from the YTD performance series (real); note has no
  // source → blank. Keep the result to reuse youPct for the YTD return tile.
  const vsMarket = deriveVsMarket(performanceSeries);
  ui.holdings.vsMarket = vsMarket || {
    youPct: null,
    spxPct: null,
    youBarPct: 0,
    spxBarPct: 0,
    note: null,
  };

  // ── benchmark inputs (FM-2101). The applicability flag and the held models are on
  //    /portfolio today; `employmentStatus` (which switches an ended plan to
  //    past-performance framing) rides on /dashboard. The finished blend is read
  //    through from the backend the moment it ships one — until then it stays null
  //    and portfolio.benchmark computes it. ──
  ui.benchmarkMeta = {
    ...emptyBenchmarkMeta(),
    applicable: api.benchmarkApplicable ?? null,
    employmentStatus: dashboard?.account?.employmentStatus ?? null,
    models: (api.models || []).map(mapModel),
    composition: api.benchmark?.composition || null,
    compositionByRange: api.benchmark?.compositionByRange || null,
    seriesByRange: api.benchmark?.seriesByRange || null,
  };

  // ── cross-endpoint enrichment (same account, optional). /dashboard fills the
  //    summary return tiles + YTD-growth; absent sources leave the blanks. ──
  const dAcct = dashboard?.account || {};
  // Plan identity isn't on /portfolio's account (only accountId/balance/asOfDate);
  // source it from /dashboard so each account shows its own plan name.
  ui.planId = dAcct.planId ?? null;
  ui.planName = dAcct.planName ?? null;
  const deltas = dashboard?.balanceTrend?.deltas || {};
  const bgb = dashboard?.balanceGrowthBreakdown;
  const delta = (k) =>
    deltas[k] ? { pct: deltas[k].pct, amount: deltas[k].amount } : { pct: null, amount: null };
  const ytdAmount = bgb
    ? (bgb.youContributedYtd || 0) + (bgb.employerMatchedYtd || 0) + (bgb.marketGrowthYtd || 0)
    : null;

  ui.summary.returns = {
    today: { pct: dAcct.dayChangePct ?? null, amount: dAcct.dayChangeAmount ?? null },
    mtd: delta('1M'), // rolling 1M ≈ MTD (dashboard has no calendar-MTD period)
    ytd: { pct: vsMarket?.youPct ?? null, amount: ytdAmount },
    oneYr: delta('1Y'),
  };

  if (bgb) {
    ui.holdings.ytdGrowth = {
      total: ytdAmount,
      breakdown: [
        // "You Contributed", not a bare "You": the pill names the money, like its
        // "Employer Contributed" neighbour (FM-3055 follow-up — the sponsor read "You" as
        // a label with no subject).
        { key: 'you', label: 'You Contributed', amount: bgb.youContributedYtd },
        // "contributed", not "match": the figure is the employer's YTD contributions, and
        // the hero's growth row says "Employer Contributed" (FM-2678: the fixed label, not
        // the employer's name) — one label for one number.
        { key: 'match', label: 'Employer Contributed', amount: bgb.employerMatchedYtd },
        { key: 'market', label: 'Market', amount: bgb.marketGrowthYtd },
      ],
    };
  }

  // ── money sources ──
  if (Array.isArray(moneySources) && moneySources.length) {
    ui.sources = moneySources.map((s) => ({
      key: s.sourceKey,
      name: s.name,
      color: s.displayColor || SOURCE_COLOR[s.sourceKey] || '#0077CC',
      total: s.total,
      contrib: s.contrib,
      ytd: s.ytd,
      growth: s.growth,
      vested: s.vested,
      vestingNote: s.vestingNote ?? null,
      desc: s.description,
      share: s.share,
      // The source's own fund balances (FM-3085); absent → By Source estimates from `share`.
      funds: Array.isArray(s.funds)
        ? s.funds.map((f) => ({ ticker: f.ticker, name: f.name, balance: f.balance, pct: f.pctOfSource ?? null }))
        : null,
    }));
  }

  // ── performance series → compare + compareDates (keyed by period) ──
  if (Array.isArray(performanceSeries) && performanceSeries.length) {
    const compare = {};
    const compareDates = {};
    performanceSeries.forEach((p) => {
      compare[p.period] = {
        you: p.youReturns,
        spx: p.benchmarkReturns,
        // Same rule as mock mode: label each tick with the date of the point it
        // sits on, falling back to the backend's own labels when it ships no dates.
        labels: axisLabels((p.youReturns || []).length, p.pointDates) || p.pointLabels,
      };
      if (p.pointDates) compareDates[p.period] = p.pointDates;
    });
    ui.compare = compare;
    ui.compareDates = compareDates;
  }

  // ── fund fact sheets → keyed by ticker ──
  const liveFundDetail = mapFundDetails(fundDetails);
  if (liveFundDetail) ui.fundDetail = liveFundDetail;

  // ── asset-class sparklines → keyed by asset class ──
  if (Array.isArray(classSparklines) && classSparklines.length) {
    ui.classSpark = Object.fromEntries(classSparklines.map((c) => [c.assetClassKey, c.series]));
  }

  // ── recent activity ──
  if (Array.isArray(activity) && activity.length) {
    ui.activity = activity.map((t) => ({
      id: t.transactionId,
      type: ACTIVITY_TYPE[t.category] || 'contribution',
      ticker: t.fundTicker ?? null, // null on a multi-fund transaction (FM-2718)
      desc: t.heading,
      date: formatDate(t.date), // ISO → "May 16, 2026"
      // The wire amount is unsigned with the sign in `direction` (FM-2718); re-sign a debit
      // here so the list reads "-$500.00", not "$500.00".
      amount: t.direction === 'neg' && t.amount != null ? -Math.abs(t.amount) : t.amount,
      signed: t.direction === 'pos',
      // Settlement status → RecentActivity flags not-yet-settled rows (FM-2098),
      // matching the hero's pending treatment for the same row.
      status: (t.status || '').toLowerCase() || null,
    }));
  }

  // Elections: "% allocated" summed from the real per-fund elections; "Changed"
  // date from /manage-account (election rules, falling back to the contribution
  // change). Per-fund pills come from funds[].
  const changedIso =
    manage?.electionRules?.lastChanged || manage?.contributionSetup?.lastChangeDate || null;
  ui.elections = {
    changedDate: changedIso ? formatDate(changedIso) : null,
    allocatedPct: deriveAllocated(holdings),
  };

  return ui;
};
